import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import DeleteIcon from "@mui/icons-material/Delete";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import PreviewIcon from "@mui/icons-material/Preview";
import RefreshIcon from "@mui/icons-material/Refresh";
import ResendIcon from "@mui/icons-material/ScheduleSend";
import { Divider, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { useSnackbar } from "notistack";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { getCampaign, getCommunicationTemplate } from "../../data/omniDataAPI";
import { searchInvites } from "../../data/omniDataAPI";
import * as AdminSessionActions from "../../state/redux/adminSession";
import { createOmniPaginationObject } from "../../utils/helpers";
import { deepCopyObject } from "../../utils/helpers";
import OmniCommunicationQueryListItem from "../ui/omniCommunicationQueryListItem";
import OmniCommunicationRequestResultsListItem from "../ui/omniCommunicationRequestResultsListItem";
import OmniDatePicker from "../ui/omniDatePicker";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniEditButton from "../ui/omniEditButton";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import SurveyInvites from "../ui/surveyInvites";
import AlertDialog from "./alertDialog";

const CommunicationRequestDialog = props => {
  const {
    open,
    dialogTitle = "Communication Request",
    cancelMessage = "Cancel",
    deleteMessage = "Remove",
    okMessage = "OK",
    maxWidth = "sm",
    fullWidth = true,
    emailTemplates = [],
    tabId = 0,
    omniODM, //omni Data Model Object, a root level object representation, Pass in a COPY, the only way this works
  } = props;

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [editedODM, setEditedODM] = useState(deepCopyObject(omniODM));
  const [sendDate, setSendDate] = useState(
    omniODM.omniData.sendDate ? new Date(omniODM.omniData.sendDate) : new Date(),
  );
  const [archivedCampaignODM, setArchivedCampaignODM] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const [readyOk, setReadyOk] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [selectedTemplateID, setSelectedTemplateID] = useState(null);
  const [descriptionValid, setDescriptionValid] = useState(false);

  const [tabIndex, setTabIndex] = useState(tabId);

  const onAcceptHandler = () => {
    editedODM.omniData.description = editedODM.omniData.description.trim();
    editedODM.omniData.communicationTemplateId = selectedTemplateID;

    editedODM.omniData.sendDate = sendDate.toISOString();

    if (editedODM._id == null) {
      editedODM.omniData.initialCount = props.commInvitePagination.totalItems;

      editedODM.omniData.destinations = {
        to: {
          partnerIds: [],
          partnerQuery: null, // # Query object for the /communicationRequest/search endpoint
          inviteIds: [],
          inviteQuery: { query: props.commInvitePagination.query }, // # Query object from /invites/search
        },
      };
    }

    props.onAccept(editedODM);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const onDeleteHandler = () => {
    props.onDelete(editedODM._id);
  };

  const [filteredTemplates, setFilteredTemplates] = useState([]);

  useEffect(() => {
    if (filteredTemplates.length > 0) {
      setSelectedTemplateID(filteredTemplates[0]._id);
    } else {
      setSelectedTemplateID(null);
    }
  }, [filteredTemplates]);

  useEffect(() => {
    try {
      if (
        editedODM._id != null &&
        editedODM.omniData.sendDate.substring(0, 10) !== sendDate.toISOString().substring(0, 10)
      ) {
        setReadyOk(true);
      }
    } catch (error) {
      console.log(error);
    }
  }, [sendDate, editedODM]);

  useEffect(() => {
    if (Object.keys(props.commInvitePagination.query).length && descriptionValid) {
      setReadyOk(true);
    } else {
      setReadyOk(false);
    }
  }, [props.commInvitePagination.query, descriptionValid, setReadyOk]);

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (props.commInviteODMs != null) {
      setRows(props.commInviteODMs);
    }
  }, [props.commInviteODMs]);

  // Check given name
  useEffect(() => {
    setFilteredTemplates(
      emailTemplates.filter(
        item => item.omniData.communicationTarget === editedODM.omniData.communicationTarget,
      ),
    );
  }, [editedODM.omniData.communicationTarget, emailTemplates]);

  const updateCommunicationRequestODM = event => {
    try {
      editedODM.omniData[event.target.id] = event.target.value;

      setEditedODM(deepCopyObject(editedODM));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    editedODM.omniData.description ? setDescriptionValid(true) : setDescriptionValid(false);
  }, [editedODM.omniData.description]);

  useEffect(() => {
    if (omniODM != null) {
      if (omniODM.omniRelationships.relatedData.length > 0) {
        //look up the Target Template
        let templateID = omniODM.omniData.communicationTemplateId;

        getCommunicationTemplate(templateID).then(theResult => {
          if (theResult) {
            setArchivedCampaignODM(theResult);
          } else {
            //snack bar the error
            console.log("communicationRequestDialog: Failed to get Template");
          }
        });
      }

      for (let index = 0; index < omniODM.omniRelationships.relatedData.length; index++) {
        if (
          omniODM.omniRelationships.relatedData[index].omniRelationshipType ===
          constants.omniRelationshipUserTypes.campaign
        ) {
          getCampaign(omniODM.omniRelationships.relatedData[index]._id).then(theResult => {
            if (theResult != null) {
              setCampaigns(prevState => {
                prevState.push(theResult);
                return prevState;
              });
            }
          });
        }
      }
    }
  }, [omniODM, setCampaigns]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>

      <DialogTabs tabIndex={tabIndex} setTabIndex={setTabIndex}>
        <Tab key={`tab0`} icon={<InfoRoundedIcon />} label={"Info"} />
        <Tab key={`tab1`} icon={<InfoRoundedIcon />} label="Results" />
      </DialogTabs>

      <DialogContent>
        {/* INFORMATION TAB */}
        <DialogTab selectedIndex={tabIndex} index={0} sx={{ overflowY: "hidden" }}>
          <Grid container>
            <Grid item xs={8} sm={8}>
              <OmniTextField
                label="Campaign"
                type="textInput"
                name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                id={"description"}
                value={editedODM.omniData.description}
                error={!descriptionValid}
                onChange={event => {
                  updateCommunicationRequestODM(event);
                }}
                placeholder={"Purpose of this communication request"}
                required
                disabled={omniODM.omniRelationships.relatedData.length > 0}
              />
            </Grid>
            <Grid item xs={4} sm={4}>
              <OmniDatePicker
                sx={{ marginLeft: theme.spacing(1) }}
                label="Send On"
                value={sendDate}
                name={uuid()} //prevents AutoComplete Suggestions
                variant="inline"
                inputVariant="outlined"
                onAccept={null}
                onChange={date => setSendDate(date)}
                format="MM/dd/yyyy"
                fullWidth
              />
            </Grid>
            {omniODM.omniRelationships.relatedData.length === 0 && (
              <Grid item xs={10} sm={11}>
                {selectedTemplateID && (
                  <OmniTextField
                    label="Communication Template"
                    // name={uuid()} //prevents AutoComplete Suggestions
                    variant="outlined"
                    // id={"communicationTarget"}
                    value={selectedTemplateID}
                    select
                    onChange={event => {
                      setSelectedTemplateID(event.target.value);
                    }}
                  >
                    {filteredTemplates.map(templateODM => {
                      return (
                        <OmniMenuItem key={templateODM._id} value={templateODM._id}>
                          {`${templateODM.omniData.name} - ${templateODM.omniData.description}`}
                        </OmniMenuItem>
                      );
                    })}
                  </OmniTextField>
                )}
                {!selectedTemplateID && <Typography>No Templates for Invitees</Typography>}
              </Grid>
            )}
            {omniODM.omniRelationships.relatedData.length === 0 && (
              <Grid item xs={2} sm={1}>
                <Box sx={{ position: "relative" }}>
                  <OmniEditButton
                    sx={{ top: 11 }}
                    title={"Copy GUID to Clipboard"}
                    disabled={selectedTemplateID == null}
                    onClick={e => {
                      e.stopPropagation();

                      let template = filteredTemplates.find(
                        temp => temp._id === selectedTemplateID,
                      );
                      if (template) {
                        window.open(template.omniData.templateUrl, "_blank", "noreferrer");
                      }
                    }}
                    icon={<PreviewIcon size="large" />}
                  />
                </Box>
              </Grid>
            )}
            {omniODM.omniRelationships.relatedData.length > 0 && (
              <Grid item xs={10} sm={11}>
                <OmniTextField
                  label="Communication Template"
                  variant="outlined"
                  value={
                    archivedCampaignODM == null
                      ? omniODM.omniData.communicationTemplateId
                      : archivedCampaignODM.omniData.name
                  }
                  disabled
                ></OmniTextField>
              </Grid>
            )}
            {omniODM.omniRelationships.relatedData.length > 0 && (
              <Grid item xs={2} sm={1}>
                <Box sx={{ position: "relative" }}>
                  <OmniEditButton
                    sx={{ top: 11 }}
                    title={"Preview"}
                    disabled={archivedCampaignODM == null}
                    onClick={e => {
                      e.stopPropagation();

                      let template = filteredTemplates.find(
                        temp => temp._id === selectedTemplateID,
                      );
                      if (template) {
                        window.open(template.omniData.templateUrl, "_blank", "noreferrer");
                      }
                    }}
                    icon={<PreviewIcon size="large" />}
                  />
                </Box>
              </Grid>
            )}

            {editedODM._id == null && (
              <Grid item xs={12}>
                <SurveyInvites
                  inviteODMs={rows}
                  inviteSurveyType={constants.inviteSurveyType.comms}
                  onRequestNewPage={props.loadCommInvitesRequest}
                  isLoading={props.commInvitePageLoading}
                  invitePagination={props.commInvitePagination}
                />
              </Grid>
            )}
            {editedODM._id != null && (
              <Grid item xs={8}>
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: theme.spacing(1.5),
                    marginBottom: theme.spacing(1),
                    maxWidth: "300px",
                  }}
                >
                  {"Query Definition: " + editedODM.omniData.initialCount + " records"}
                </Typography>
              </Grid>
            )}
            {editedODM._id != null && (
              <Grid item xs={4}>
                <Box
                  sx={{
                    width: "100%",
                    marginTop: theme.spacing(1),
                    display: "flex",
                    flexDirection: "row-reverse",
                    paddingRight: theme.spacing(1),
                  }}
                >
                  <OmniResponsiveButton
                    icon={<RefreshIcon />}
                    onClick={() => {
                      //call the query
                      //updateCommunicationRequestODM(event);

                      let newPage = createOmniPaginationObject(null, 10);

                      newPage.pageIndex = 1;
                      newPage.pageSize = 5;

                      let newQuery = editedODM.omniData.destinations.to.inviteQuery.query;

                      newPage.query = newQuery;
                      newPage.sort = {};

                      searchInvites(newPage)
                        .then(results => {
                          if (results.pageInfo.itemCount != null) {
                            let event = {
                              target: { id: "initialCount", value: results.pageInfo.itemCount },
                            };
                            enqueueSnackbar(`Query Returned ${results.pageInfo.itemCount} items.`, {
                              variant: "success",
                            });
                            updateCommunicationRequestODM(event);
                            if (results.pageInfo.itemCount !== editedODM.omniData.initialCount) {
                              setReadyOk(true);
                            }
                          }
                        })
                        .catch(err => {
                          enqueueSnackbar(`Error: ${err}`, {
                            variant: "error",
                          });
                        });
                    }}
                    buttonText={"Run Query"}
                    tooltipTitle=""
                  />
                </Box>
              </Grid>
            )}

            {editedODM._id != null && (
              <Grid item xs={12}>
                <OmniCommunicationQueryListItem
                  inviteQuery={editedODM.omniData.destinations.to.inviteQuery}
                />
                {/* <Typography sx={{ maxHeight: "300px", overflowY: "auto", whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(editedODM.omniData.destinations.to.inviteQuery, null, "\t")}
                </Typography> */}
              </Grid>
            )}
          </Grid>
        </DialogTab>

        {/* Results TAB */}
        <DialogTab selectedIndex={tabIndex} index={1}>
          <Grid container>
            <Grid item xs={10} sm={11}>
              <Typography
                variant="h6"
                sx={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }}
              >
                {`Campaign Results: ${campaigns.length}`}
              </Typography>
            </Grid>
            <Grid item xs={2} sm={1}>
              <Box sx={{ position: "relative" }}>
                <OmniEditButton
                  sx={{ top: 8 }}
                  title={"Copy GUID to Clipboard"}
                  disabled={selectedTemplateID == null}
                  onClick={e => {
                    e.stopPropagation();

                    setShowResend(true);
                  }}
                  icon={<ResendIcon size="large" />}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ maxHeight: "300px", overflowY: "auto", whiteSpace: "pre-wrap" }}>
                {campaigns.map((campaign, index) => {
                  return (
                    <OmniCommunicationRequestResultsListItem
                      communicationRequestResultsDef={campaign}
                      index={index}
                      key={index}
                    />
                  );
                })}
              </Typography>
            </Grid>
          </Grid>
        </DialogTab>
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {editedODM._id != null && props.onDelete != null && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={!readyOk}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this Communication Request?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
      {showResend && (
        <AlertDialog
          open={showResend}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to RESEND this Communication Request?"}
          onAccept={() => {
            props.onResend();
            setShowResend(false);
          }}
          onClose={() => {
            setShowResend(false);
          }}
        />
      )}
    </Dialog>
  );
};

const mapStateToProps = state => ({
  communicationTemplateODMs: state.adminSession.communicationTemplateODMs,
  communicationRequestODMs: state.adminSession.communicationRequestODMs,

  commInviteODMs: state.adminSession.commInviteODMs,
  commInvitePageLoading: state.adminSession.commInvitePageLoading,
  commInvitePagination: state.adminSession.commInvitePagination,
});

const mapDispatchToProps = {
  prosecuteCampaignRequest: AdminSessionActions.prosecuteCampaignRequest,
  loadCommunicationRequestsRequest: AdminSessionActions.loadCommunicationRequestsRequest,
  updateCommunicationRequestRequest: AdminSessionActions.updateCommunicationRequestRequest,
  deleteCommunicationRequestRequest: AdminSessionActions.deleteCommunicationRequestRequest,

  loadCommInvitesRequest: AdminSessionActions.loadCommInvitesRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(CommunicationRequestDialog);
